import React, {
    useMemo,
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@shadcn-in-nx/ui/tooltip";
import { useChatContext } from "../ChatContext";
import { useWorkflow } from "../../context/WorkflowContext";
import { useAuth } from "../../context/AuthContext";
import {
    LayoutDashboard,
    Eye,
    ExternalLink,
    Smartphone,
    Monitor,
    X,
    MousePointer,
} from "lucide-react";
import { Bluelagoon, useBluelagoon } from "@bluelagoon";
import { track } from "@crm/analytics";

import PublishButton from "../../components/PublishButton";
import AuditLogsButton from "./AuditLogsButton";
import PageSelector from "./PageSelector";
import EnvironmentSelector from "./EnvironmentSelector";
import useWorkflowPreviewToken from "../../hooks/useWorkflowPreviewToken";

const AppV2Header = () => {
    const { t } = useTranslation();
    const {
        appViewMode,
        setAppViewMode,
        previewDevice,
        setPreviewDevice,
        selectedEnvironment,
        setSelectedEnvironment,
        components,
        isSelectMode,
        toggleSelectMode,
        environments,
    } = useChatContext();
    const { workflow } = useWorkflow();
    const { issueLongLivedToken } = useWorkflowPreviewToken(workflow?.id);
    const { user } = useAuth();
    const [showTutorialTooltip, setShowTutorialTooltip] = useState(false);
    const tutorialButtonRef = useRef(null);

    const { call: markTutorialComplete } = useBluelagoon({
        method: "markTutorialComplete",
    });

    // Check if tutorial should be shown
    useEffect(() => {
        // Reset tooltip state when conditions change
        setShowTutorialTooltip(false);

        if (
            workflow?.workflowType === "APP_V2" &&
            components &&
            components.length > 0
        ) {
            // Check if user has completed the tutorial from auth context
            const hasCompletedTutorialFromUser =
                user?.tutorials?.appV2Header?.completed;
            // Fallback to localStorage check
            const tutorialKey = `appV2HeaderTutorial`;
            const hasCompletedTutorialFromStorage =
                localStorage.getItem(tutorialKey) === "true";
            const hasCompletedTutorial =
                hasCompletedTutorialFromUser || hasCompletedTutorialFromStorage;

            if (!hasCompletedTutorial) {
                // Small delay to ensure buttons are rendered
                const timeoutId = setTimeout(() => {
                    setShowTutorialTooltip(true);
                }, 500);

                // Cleanup function to clear timeout on unmount or dependency change
                return () => {
                    clearTimeout(timeoutId);
                    setShowTutorialTooltip(false);
                };
            }
        }
    }, [workflow?.workflowType, workflow?.id, components, user?.tutorials]);

    // Mark tutorial as complete
    const handleTutorialComplete = useCallback(async () => {
        if (!workflow?.id) return;

        const tutorialKey = `appV2HeaderTutorial`;
        localStorage.setItem(tutorialKey, "true");
        setShowTutorialTooltip(false);

        try {
            await markTutorialComplete({
                tutorialKey: "appV2Header",
                completed: true,
            });
        } catch (error) {
            console.error("Error marking tutorial as complete:", error);
        }
    }, [workflow?.id, markTutorialComplete]);

    // Determine component server URL based on environment
    const { componentServerUrl, useCustomDomain } = useMemo(() => {
        const currentEnvironment = selectedEnvironment
            ? environments?.find((e) => e.name === selectedEnvironment)
            : null;
        return Bluelagoon.getComponentServerUrl(
            workflow?.internalDomain,
            currentEnvironment?.customDomain
        );
    }, [workflow?.internalDomain, selectedEnvironment, environments]);

    const handleOpenApp = async () => {
        handleTutorialComplete();
        const stepId = workflow?.steps?.[0]?.id;
        const currentVersion = workflow?.steps?.[0]?.currentVersion;

        if (!stepId) return;

        // Use environment-specific version if not production
        let displayVersion = currentVersion;
        if (selectedEnvironment && selectedEnvironment !== "production") {
            const env = environments?.find(
                (e) => e.name === selectedEnvironment
            );
            if (env?.currentWorkflowStepVersionId) {
                displayVersion = env.currentWorkflowStepVersionId;
            }
        }

        const basePath = useCustomDomain
            ? componentServerUrl
            : `${componentServerUrl}/apps/${stepId}`;
        const token = await issueLongLivedToken();
        const params = new URLSearchParams({
            v: String(displayVersion || Date.now()),
        });
        if (token) params.set("x_deep_link_token", token);
        window.open(`${basePath}?${params.toString()}`, "_blank");
    };

    if (workflow?.workflowType !== "APP_V2") {
        return null;
    }

    return (
        <div className="grid grid-cols-[auto_1fr_auto] items-center w-full gap-1 md:gap-2 lg:gap-4 px-2 md:px-3 py-1 border-b border-gray-200 h-12 min-w-0">
            {/* Left: View Mode Switcher */}
            <div className="flex justify-start items-center gap-1 md:gap-2 relative">
                <TooltipProvider>
                    <div
                        ref={tutorialButtonRef}
                        className="bg-gray-100 rounded-lg p-1 flex gap-0.5"
                    >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={() => setAppViewMode("preview")}
                                    className={`p-1.5 rounded-md transition-colors flex items-center gap-1 ${
                                        appViewMode !== "dashboard"
                                            ? "bg-white shadow-sm text-blue-600"
                                            : "text-gray-500 hover:text-gray-900"
                                    }`}
                                >
                                    <Eye className="w-4 h-4" />
                                    {appViewMode !== "dashboard" && (
                                        <span className="text-xs hidden xl:inline">
                                            {t("appPreview.preview") ||
                                                "Preview"}
                                        </span>
                                    )}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t("appPreview.preview") || "Preview"}</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip
                            open={showTutorialTooltip ? true : undefined}
                            onOpenChange={
                                showTutorialTooltip
                                    ? (open) => {
                                          if (!open) {
                                              handleTutorialComplete();
                                          }
                                      }
                                    : undefined
                            }
                        >
                            <TooltipTrigger asChild>
                                <button
                                    onClick={() => {
                                        handleTutorialComplete();
                                        setAppViewMode("dashboard");
                                        // Track dashboard view clicked
                                        track("Dashboard View Clicked", {
                                            workflowId: workflow?.id,
                                            workflowType:
                                                workflow?.workflowType,
                                        });
                                    }}
                                    className={`p-1.5 rounded-md transition-all flex items-center gap-1 relative ${
                                        appViewMode === "dashboard"
                                            ? "bg-white shadow-sm text-blue-600"
                                            : "text-gray-500 hover:text-gray-900"
                                    } ${
                                        showTutorialTooltip
                                            ? "ring-2 ring-blue-500 ring-offset-2 z-10"
                                            : ""
                                    }`}
                                >
                                    <LayoutDashboard className="w-4 h-4" />
                                    <span className="text-xs font-medium hidden xl:inline">
                                        {t("appPreview.dashboard") ||
                                            "Dashboard"}
                                    </span>
                                </button>
                            </TooltipTrigger>
                            <TooltipContent
                                side="bottom"
                                align="center"
                                sideOffset={8}
                                className="bg-blue-600 text-white border-blue-600 max-w-xs relative shadow-lg"
                            >
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleTutorialComplete();
                                    }}
                                    className="absolute top-1 right-1 p-1 rounded hover:bg-blue-700 transition-colors"
                                    aria-label={t("common.close") || "Close"}
                                >
                                    <X className="w-3 h-3" />
                                </button>
                                <p className="font-medium mb-1 pr-6">
                                    {t("appPreview.tutorial.title") ||
                                        "Manage your app"}
                                </p>
                                <p className="text-sm">
                                    {t("appPreview.tutorial.message") ||
                                        "Click here to manage your app settings"}
                                </p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    className="p-1.5 rounded-md transition-colors text-gray-500 hover:text-gray-900"
                                    onClick={handleOpenApp}
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t("appPreview.openApp") || "Open App"}</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </TooltipProvider>
            </div>

            {/* Center: Page Selector + Device Toggle */}
            <div className="flex items-center justify-center gap-1 md:gap-2 min-w-0 overflow-hidden">
                {appViewMode !== "dashboard" ? (
                    <div className="flex items-center gap-1 md:gap-2 min-w-0">
                        <PageSelector />

                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={toggleSelectMode}
                                        className={`p-1.5 rounded-md transition-colors flex items-center gap-1 ${
                                            isSelectMode
                                                ? "bg-blue-500 text-white shadow-sm"
                                                : "bg-white shadow-sm text-gray-700 hover:text-gray-900"
                                        }`}
                                    >
                                        <MousePointer className="w-4 h-4" />
                                        {isSelectMode && (
                                            <span className="text-xs font-medium hidden lg:inline">
                                                Select
                                            </span>
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>
                                        {isSelectMode
                                            ? "Exit select mode"
                                            : "Select element to reference"}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={() =>
                                            setPreviewDevice((prev) =>
                                                prev === "mobile"
                                                    ? "desktop"
                                                    : "mobile"
                                            )
                                        }
                                        className="p-1.5 rounded-md transition-colors flex items-center bg-white shadow-sm text-gray-700 hover:text-gray-900"
                                    >
                                        {previewDevice === "mobile" ? (
                                            <Smartphone className="w-4 h-4" />
                                        ) : (
                                            <Monitor className="w-4 h-4" />
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>
                                        {previewDevice === "mobile"
                                            ? t("appPreview.mobile") ||
                                              "Mobile view"
                                            : t("appPreview.desktop") ||
                                              "Full width"}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                ) : (
                    <div className="hidden md:flex w-auto max-w-[200px] h-8 bg-gray-50 rounded-md border border-gray-200 items-center justify-center px-3">
                        <p className="text-sm font-medium text-gray-600 truncate">
                            {appViewMode === "dashboard"
                                ? t("appPreview.dashboard") || "Dashboard"
                                : t("appPreview.preview") || "Preview"}
                        </p>
                    </div>
                )}
            </div>

            {/* Right: Action Buttons */}
            <div className="flex justify-end items-center gap-1 flex-shrink-0">
                <EnvironmentSelector
                    selectedEnvironment={selectedEnvironment}
                    onEnvironmentChange={setSelectedEnvironment}
                    showLockStatus={false}
                />
                <PublishButton />
            </div>
        </div>
    );
};

export default AppV2Header;
